import axios, {
  type AxiosError,
  type AxiosResponse,
  type InternalAxiosRequestConfig,
} from "axios";
import { API_BASE_URL } from "../config/env";
import { tokenStore } from "./tokenStore";
import type { ApiEnvelope, PaginatedResult, PaginationMeta } from "../types/api";

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 20000,
  headers: { "Content-Type": "application/json" },
});

// ---- Request: attach the Bearer access token ----
api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = tokenStore.getAccess();
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

// ---- Response: on 401, refresh once (single-flight), then retry ----
// Concurrent 401s share one in-flight refresh so we don't stampede the endpoint.
let refreshPromise: Promise<string> | null = null;

function forceLogout(): void {
  tokenStore.clear();
  if (window.location.pathname !== "/login") {
    window.location.assign("/login");
  }
}

async function refreshAccessToken(): Promise<string> {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) throw new Error("no refresh token");

  // Bare axios (not `api`) to skip interceptors and avoid recursion.
  const res = await axios.post<
    ApiEnvelope<{ accessToken: string; refreshToken: string }>
  >(`${API_BASE_URL}/auth/admin/refresh-token`, { refreshToken });

  const data = res.data?.data;
  if (!data?.accessToken) throw new Error("refresh failed");
  tokenStore.setTokens(data.accessToken, data.refreshToken ?? refreshToken);
  return data.accessToken;
}

api.interceptors.response.use(
  (res) => res,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retry?: boolean })
      | undefined;
    const status = error.response?.status;

    // Don't try to refresh the refresh/login calls themselves.
    const url = original?.url ?? "";
    const isAuthCall =
      url.includes("/auth/admin/refresh-token") ||
      url.includes("/auth/admin/login");

    if (status === 401 && original && !original._retry && !isAuthCall) {
      original._retry = true;
      try {
        if (!refreshPromise) {
          refreshPromise = refreshAccessToken().finally(() => {
            refreshPromise = null;
          });
        }
        const newToken = await refreshPromise;
        original.headers.set("Authorization", `Bearer ${newToken}`);
        return api(original);
      } catch {
        forceLogout();
        return Promise.reject(error);
      }
    }

    return Promise.reject(error);
  },
);

// ---- Envelope helpers ----

/** Unwrap `{ success, data }` → data; throw a friendly message on failure. */
export function unwrap<T>(res: AxiosResponse<ApiEnvelope<T>>): T {
  const body = res.data;
  if (!body?.success) {
    throw new Error(messageOf(body));
  }
  return body.data as T;
}

/** Human-readable message from a backend envelope or axios error. */
export function messageOf(body: ApiEnvelope<unknown> | undefined): string {
  if (!body) return "Request failed";
  const m = body.message;
  if (typeof m === "string") return m;
  if (m && typeof m === "object") return m.en;
  return "Request failed";
}

/** Pull a readable error message out of an unknown thrown value. */
export function errorMessage(err: unknown): string {
  if (axios.isAxiosError(err)) {
    // Rate limited (Express limiter or the proxy in front of it). Say so
    // plainly rather than surfacing a generic failure.
    if (err.response?.status === 429) {
      const header = Number(err.response.headers?.["retry-after"]);
      const body = err.response.data as { retryAfter?: number } | undefined;
      const wait = Number.isFinite(header) && header > 0 ? header : body?.retryAfter;
      return wait
        ? `Too many requests. Please wait ${wait} second${wait === 1 ? "" : "s"} and try again.`
        : "Too many requests. Please wait a moment and try again.";
    }
    const body = err.response?.data as ApiEnvelope<unknown> | undefined;
    if (body) return messageOf(body);
    return err.message;
  }
  if (err instanceof Error) return err.message;
  return "Something went wrong";
}

/**
 * Normalise the several backend list shapes into PaginatedResult<T>:
 *  - data:[...] + meta.pagination          (drivers, users, trips, coupons…)
 *  - data:{ <key>:[...], total, page, totalPages }   (admins)
 *  - data:[...] with no pagination meta     (ride types)
 */
export function toPaginated<T>(
  res: AxiosResponse<ApiEnvelope<unknown>>,
  arrayKey?: string,
): PaginatedResult<T> {
  const body = res.data;
  if (!body?.success) throw new Error(messageOf(body));

  const data = body.data;
  const pagination = body.meta?.pagination as PaginationMeta | undefined;

  if (Array.isArray(data)) {
    const items = data as T[];
    return {
      items,
      total: pagination?.total ?? items.length,
      page: pagination?.page ?? 1,
      totalPages: pagination?.totalPages ?? 1,
    };
  }

  if (data && typeof data === "object") {
    const obj = data as Record<string, unknown>;
    const arr = (arrayKey && Array.isArray(obj[arrayKey])
      ? obj[arrayKey]
      : Object.values(obj).find((v) => Array.isArray(v))) as T[] | undefined;
    const items = arr ?? [];
    return {
      items,
      total: (obj.total as number) ?? pagination?.total ?? items.length,
      page: (obj.page as number) ?? pagination?.page ?? 1,
      totalPages:
        (obj.totalPages as number) ?? pagination?.totalPages ?? 1,
    };
  }

  return { items: [], total: 0, page: 1, totalPages: 1 };
}

/** Drop undefined/empty params so we don't send `?search=` etc. */
export function cleanParams(
  params: Record<string, unknown>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    if (
      typeof v === "string" ||
      typeof v === "number" ||
      typeof v === "boolean"
    ) {
      out[k] = v;
    }
  }
  return out;
}
